import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { QuestScheduleViewModel } from "@playcharge/common/models/backend/quests/backoffice/quest-schedule-view-model";
import { QuestScheduleCreateModel } from "@playcharge/common/models/backend/quests/backoffice/quest-schedule-create-model";
import { QuestScheduleEditModel } from "@playcharge/common/models/backend/quests/backoffice/quest-schedule-edit-model";
import { QuestTemplateViewModel } from "@playcharge/common/models/backend/quests/backoffice/quest-template-view-model";
import { QuestTemplateEditModel } from "@playcharge/common/models/backend/quests/backoffice/quest-template-edit-model";
import { QuestViewModel } from "@playcharge/common/models/backend/quests/backoffice/quest-view-model";
import { QuestCreateModel } from "@playcharge/common/models/backend/quests/backoffice/quest-create-model";
import { QuestEditModel } from "@playcharge/common/models/backend/quests/backoffice/quest-edit-model";
import { ApiUtils } from "../utils/api-utils";
import { ApiRoutes } from "../models/constants/api-routes";

@Injectable({
    providedIn: "root",
})
export class ApiQuestsService {
    private _httpClient = inject(HttpClient);

    public getSchedules(): Observable<QuestScheduleViewModel[]> {
        return this._httpClient.get<QuestScheduleViewModel[]>(
            ApiUtils.buildUrl(ApiRoutes.quests.schedules.root),
        );
    }

    public getSchedule(scheduleId: number): Observable<QuestScheduleViewModel> {
        return this._httpClient.get<QuestScheduleViewModel>(
            ApiUtils.buildUrl(
                ApiRoutes.quests.schedules.schedule,
                new Map<string, string>([["scheduleId", scheduleId.toString()]]),
            ),
        );
    }

    public createSchedule(model: QuestScheduleCreateModel): Observable<QuestScheduleViewModel> {
        return this._httpClient.post<QuestScheduleViewModel>(
            ApiUtils.buildUrl(ApiRoutes.quests.schedules.root),
            model,
        );
    }

    public editSchedule(model: QuestScheduleEditModel): Observable<QuestScheduleViewModel> {
        return this._httpClient.put<QuestScheduleViewModel>(
            ApiUtils.buildUrl(ApiRoutes.quests.schedules.root),
            model,
        );
    }

    public getTemplates(): Observable<QuestTemplateViewModel[]> {
        return this._httpClient.get<QuestTemplateViewModel[]>(
            ApiUtils.buildUrl(ApiRoutes.quests.templates.root),
        );
    }

    public getTemplate(templateId: number): Observable<QuestTemplateViewModel> {
        return this._httpClient.get<QuestTemplateViewModel>(
            ApiUtils.buildUrl(
                ApiRoutes.quests.templates.template,
                new Map<string, string>([["templateId", templateId.toString()]]),
            ),
        );
    }

    public createTemplate(model: QuestTemplateEditModel): Observable<QuestTemplateViewModel> {
        return this._httpClient.post<QuestTemplateViewModel>(
            ApiUtils.buildUrl(ApiRoutes.quests.templates.root),
            model,
        );
    }

    public editTemplate(model: QuestTemplateEditModel): Observable<QuestTemplateViewModel> {
        return this._httpClient.put<QuestTemplateViewModel>(
            ApiUtils.buildUrl(ApiRoutes.quests.templates.root),
            model,
        );
    }

    public getQuests(): Observable<QuestViewModel[]> {
        return this._httpClient.get<QuestViewModel[]>(ApiUtils.buildUrl(ApiRoutes.quests.root));
    }

    public getQuest(questId: number): Observable<QuestViewModel> {
        return this._httpClient.get<QuestViewModel>(
            ApiUtils.buildUrl(
                ApiRoutes.quests.quest,
                new Map<string, string>([["questId", questId.toString()]]),
            ),
        );
    }

    public createQuest(model: QuestCreateModel): Observable<QuestViewModel> {
        return this._httpClient.post<QuestViewModel>(
            ApiUtils.buildUrl(ApiRoutes.quests.root),
            model,
        );
    }

    public editQuest(model: QuestEditModel): Observable<QuestViewModel> {
        return this._httpClient.put<QuestViewModel>(
            ApiUtils.buildUrl(ApiRoutes.quests.root),
            model,
        );
    }
}
